import { CapacityDetails, CapacityHealth, CapacitySummary } from 'capacity/types'
import {
    CapacityGateway,
    CapacityMembers,
    GatewayCapabilities,
    SavePlan,
    SaveResult,
    TodayUsage,
} from './capacityGateway'
import { CLIENT_SIDE_CAPABILITIES, ClientSideCapacityGateway, ClientSideGatewayDeps } from './clientSideGateway'
import { SERVER_CAPABILITIES, ServerCapacityGateway } from './serverGateway'

export const CAPACITY_SERVER_FLAGS = {
    members: 'CapacityServerMembers',
    save: 'CapacityServerSave',
    delete: 'CapacityServerDelete',
    health: 'CapacityServerHealth',
    usage: 'CapacityServerUsage',
} as const

export type CapacityServerFlags = Record<keyof typeof CAPACITY_SERVER_FLAGS, boolean>

export const NO_SERVER_FLAGS: CapacityServerFlags = {
    members: false,
    save: false,
    delete: false,
    health: false,
    usage: false,
}

export const capabilitiesFor = (flags: CapacityServerFlags): GatewayCapabilities => ({
    members: flags.members ? SERVER_CAPABILITIES.members : CLIENT_SIDE_CAPABILITIES.members,
    optionItemsCap: flags.members ? SERVER_CAPABILITIES.optionItemsCap : CLIENT_SIDE_CAPABILITIES.optionItemsCap,
    atomicSave: flags.save ? SERVER_CAPABILITIES.atomicSave : CLIENT_SIDE_CAPABILITIES.atomicSave,
    delete: flags.delete ? SERVER_CAPABILITIES.delete : CLIENT_SIDE_CAPABILITIES.delete,
    usage: flags.usage ? SERVER_CAPABILITIES.usage : CLIENT_SIDE_CAPABILITIES.usage,
    health: flags.health ? SERVER_CAPABILITIES.health : CLIENT_SIDE_CAPABILITIES.health,
})

export function createCapacityGateway(deps: ClientSideGatewayDeps, flags: CapacityServerFlags): CapacityGateway {
    const client = new ClientSideCapacityGateway(deps)
    const server = new ServerCapacityGateway()
    return {
        capabilities: capabilitiesFor(flags),
        listCapacities(accountSlug: string): Promise<CapacitySummary[]> {
            return flags.health ? server.listCapacities() : client.listCapacities(accountSlug)
        },
        getCapacity(accountSlug: string, capacityId: string): Promise<CapacityDetails> {
            return flags.health ? server.getCapacity() : client.getCapacity(accountSlug, capacityId)
        },
        getMembers(accountSlug: string, capacityId: string): Promise<CapacityMembers> {
            return flags.members ? server.getMembers() : client.getMembers(accountSlug, capacityId)
        },
        save(accountSlug: string, plan: SavePlan): Promise<SaveResult> {
            return flags.save ? server.save() : client.save(accountSlug, plan)
        },
        deleteCapacity(): Promise<void> {
            return flags.delete ? server.deleteCapacity() : client.deleteCapacity()
        },
        getTodayUsage(accountSlug: string, capacityIds: string[], date: string): Promise<Record<string, TodayUsage>> {
            return flags.usage ? server.getTodayUsage() : client.getTodayUsage(accountSlug, capacityIds, date)
        },
        getHealth(): CapacityHealth {
            return flags.health ? server.getHealth() : client.getHealth()
        },
    }
}
